import { Link } from "react-router-dom";
import { Globe, Smartphone, Monitor, ShoppingCart, Zap, Shield, Code, Palette, Layers, ArrowRight } from "lucide-react";
import Navbar from "../../components/Navbar";
import PageMeta from "../../components/PageMeta";
import Footer from "../../components/Footer";
import FloatingFAQ from "../../components/FloatingFAQ";
import ConsultationForm from "../../components/ConsultationForm";

const features = [
  { icon: Monitor, title: "Responsive Design", description: "Every website we build looks stunning on all devices." },
  { icon: Zap, title: "Lightning Fast", description: "Optimized for speed with sub-2 second load times." },
  { icon: Shield, title: "Secure & Reliable", description: "Built with security best practices and SSL certificates." },
  { icon: Palette, title: "Custom Design", description: "Unique designs tailored to your brand - no templates." },
  { icon: Code, title: "Clean Code", description: "Well-structured, maintainable code that scales." },
  { icon: Layers, title: "SEO Ready", description: "Built with SEO best practices for maximum visibility." }
];

const services = [
  { title: "Landing Pages", description: "High-converting single pages designed to capture leads.", price: "From $500" },
  { title: "Business Websites", description: "Professional multi-page websites that establish credibility.", price: "From $999" },
  { title: "E-Commerce", description: "Full online stores with payment processing and inventory.", price: "From $2,499" },
  { title: "Web Applications", description: "Custom web apps with user authentication and databases.", price: "From $4,999" }
];

const devProcess = [
  { step: "01", title: "Discovery", description: "We learn about your business, goals, and competition." },
  { step: "02", title: "Design", description: "Our designers create wireframes and mockups." },
  { step: "03", title: "Development", description: "We build your website using modern technologies." },
  { step: "04", title: "Launch", description: "After testing, we deploy and provide training." }
];

const technologies = ["React", "Next.js", "Vue.js", "Node.js", "Python", "WordPress", "Shopify", "Webflow", "Tailwind CSS", "PostgreSQL", "MongoDB", "AWS"];

export default function WebDevelopment() {
  return (
    <div className="noise-bg">
      <PageMeta
        title="Web Development | Averoan Technologies"
        description="Custom websites from $500: landing pages, business sites, e-commerce, and web apps. Fast, secure, mobile-friendly."
      />
      <Navbar />

      <section className="pt-32 pb-16 relative" data-testid="webdev-hero">
        <div className="hero-glow absolute top-0 right-0 opacity-30" />
        <div className="container-main relative z-10">
          <div className="max-w-4xl">
            <span className="tag mb-6">Web Development</span>
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold mb-6" style={{ fontFamily: '"Clash Display", sans-serif' }}>
              Websites that<br /><span style={{ color: '#D4FF00' }}>convert</span>
            </h1>
            <p className="text-base md:text-lg max-w-xl mb-8" style={{ color: '#A1A1AA' }}>
              From stunning landing pages to complex web applications, we build fast, secure, and scalable websites.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="#get-started" className="btn-primary" data-testid="webdev-cta">Get Started <ArrowRight size={16} /></a>
              <Link to="/portfolio" className="btn-outline">View Our Work</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding" style={{ background: '#18181B' }}>
        <div className="container-main">
          <div className="mb-12">
            <span className="tag mb-4">Why Choose Us</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-4" style={{ fontFamily: '"Clash Display", sans-serif' }}>Built for success</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature) => (
              <div key={feature.title} className="card">
                <div className="relative z-10">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{ background: 'rgba(212, 255, 0, 0.1)', border: '1px solid rgba(212, 255, 0, 0.2)' }}>
                    <feature.icon size={22} style={{ color: '#D4FF00' }} />
                  </div>
                  <h3 className="text-lg font-bold mb-2 normal-case" style={{ fontFamily: '"Clash Display", sans-serif' }}>{feature.title}</h3>
                  <p className="text-sm" style={{ color: '#A1A1AA' }}>{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding">
        <div className="container-main">
          <div className="mb-12">
            <span className="tag mb-4">What We Offer</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-4" style={{ fontFamily: '"Clash Display", sans-serif' }}>Our services</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((service) => (
              <div key={service.title} className="p-8 rounded-xl" style={{ background: '#18181B', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-bold normal-case" style={{ fontFamily: '"Clash Display", sans-serif' }}>{service.title}</h3>
                  <span style={{ color: '#D4FF00', fontSize: '14px', fontWeight: '600' }}>{service.price}</span>
                </div>
                <p style={{ color: '#A1A1AA' }}>{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding" style={{ background: '#18181B' }}>
        <div className="container-main">
          <div className="mb-12">
            <span className="tag mb-4">Our Process</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-4" style={{ fontFamily: '"Clash Display", sans-serif' }}>How we work</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {devProcess.map((item) => (
              <div key={item.step}>
                <div className="text-5xl font-bold mb-4" style={{ fontFamily: '"Clash Display", sans-serif', color: '#D4FF00' }}>{item.step}</div>
                <h3 className="text-xl font-bold mb-2 normal-case" style={{ fontFamily: '"Clash Display", sans-serif' }}>{item.title}</h3>
                <p className="text-sm" style={{ color: '#A1A1AA' }}>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding">
        <div className="container-main">
          <div className="text-center mb-12">
            <span className="tag mb-4">Tech Stack</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-4" style={{ fontFamily: '"Clash Display", sans-serif' }}>Technologies we use</h2>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            {technologies.map((tech) => (
              <span key={tech} className="px-6 py-3 rounded-full text-sm" style={{ background: '#18181B', border: '1px solid rgba(255,255,255,0.1)', color: '#A1A1AA' }}>{tech}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Consultation Form */}
      <section id="get-started" className="section-padding" style={{ background: '#18181B' }} data-testid="webdev-consultation">
        <div className="container-main">
          <div className="text-center mb-12">
            <span className="tag mb-4">Get Started</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-4" style={{ fontFamily: '"Clash Display", sans-serif' }}>
              Ready to build <span style={{ color: '#D4FF00' }}>your website?</span>
            </h2>
            <p className="text-base md:text-lg mt-4" style={{ color: '#A1A1AA' }}>
              Tell us about your project and book a free consultation.
            </p>
          </div>
          <ConsultationForm preSelectedServiceId="web" />
        </div>
      </section>

      <Footer />
      <FloatingFAQ />
    </div>
  );
}
